import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useDomainStore, useFamilyStore } from '@/hooks/useStores';
import { petsApi } from '@/api/petsWrapper';
import { mediaApi } from '@/api/mediaWrapper';
import { peopleApi } from '@/api/peopleWrapper';
import PetEditForm from '@/components/pet/PetEditForm';
import PetProfileOwners, { type PetProfileOwnerLink } from '@/components/pet/PetProfileOwners';
import ProfilePhotoUpload from '@/components/person/ProfilePhotoUpload';
import ProfileMoreMenu from '@/components/person/ProfileMoreMenu';
import ScreenHelpDot from '@/components/help/ScreenHelpDot';
import RoleExplanationModal from '@/components/modals/RoleExplanationModal';
import ManageMemberAccessModal from '@/components/modals/ManageMemberAccessModal';
import type { PetResponse } from 'we-tree-api-client/v2';
import { formatTemporal, calendarYearFromStoredDate, localCalendarDateFromStored } from '@/utils/dateFormatting';
import type { DatePrecision } from '@/utils/dateFormatting';
import { duration } from '@/utils/duration';
import {
  memberAccessFromRoleExplanation,
  getMemberAccessPrefix,
  type MemberAccessState
} from '@/utils/rolePermissions';
import { petCategory } from '@/translations/en';
import type { ProfileLocationState } from '@/types/profile-navigation';
import { Lock, Unlock } from '@/icons';
import Target from '@/icons/Target';
import Pencil from '@/icons/Pencil';

import './pet-profile-screen.css';

function petProfileInitials(displayName?: string | null): string {
  const parts = (displayName ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  const one = parts[0] ?? '';
  return (one.slice(0, 2) || '?').toUpperCase();
}

const PetProfileScreen: React.FC = observer(() => {
  const { t } = useTranslation('screens');
  const { t: tForms } = useTranslation('forms');
  const { t: tCommon } = useTranslation('common');
  const { t: tLabels } = useTranslation('labels');
  const domainStore = useDomainStore();
  const familyStore = useFamilyStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { familyId: familyIdParam, familyMemberId: familyMemberIdParam } = useParams<{
    familyId: string;
    familyMemberId: string;
  }>();

  const locationState = location.state as ProfileLocationState | undefined;

  const [petData, setPetData] = useState<PetResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [memberAccess, setMemberAccess] = useState<MemberAccessState>({
    effectiveRole: null,
    accessMode: 'view',
    canDirectModify: false,
    canSuggest: false,
    canReview: false,
    canManageAccess: false
  });
  const [showRoleExplanation, setShowRoleExplanation] = useState(false);
  const [showManageAccess, setShowManageAccess] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [photoDisplayStatus, setPhotoDisplayStatus] = useState<'safe' | 'processing' | 'unavailable' | null>(null);

  const effectiveRoleLabel = (role: string | null): string => {
    if (!role) return t('petProfile.roleMember');
    if (role === 'viewer') return t('petProfile.roleViewer');
    const key = `role.${role}` as const;
    const translated = tLabels(key);
    return translated === key ? role.charAt(0).toUpperCase() + role.slice(1) : translated;
  };

  function formatPetKind(category: string, subType?: string | null): string {
    const label = petCategory[category as keyof typeof petCategory] ?? category;
    if (subType?.trim()) {
      return `${label} · ${subType.trim()}`;
    }
    return label;
  }

  function formatSexLabel(sex: string | undefined): string | null {
    if (!sex || sex === 'unknown') return null;
    if (sex === 'male') return tForms('petSex.male');
    if (sex === 'female') return tForms('petSex.female');
    return sex.charAt(0).toUpperCase() + sex.slice(1);
  }

  useEffect(() => {
    if (!familyIdParam || !familyMemberIdParam) return;
    if (!locationState?.from) return;

    try {
      sessionStorage.setItem(
        `profileBackFrom_${familyIdParam}`,
        JSON.stringify({
          screen: 'pets',
          familyMemberId: familyMemberIdParam,
          from: locationState.from,
          originalAnchorId: locationState.originalAnchorId
        })
      );
    } catch {
      // Ignore storage errors (private mode, etc.)
    }
  }, [familyIdParam, familyMemberIdParam, locationState?.from, locationState?.originalAnchorId]);

  useEffect(() => {
    const loadPetData = async () => {
      if (!familyIdParam || !familyMemberIdParam) {
        setErrorMessage(t('petProfile.missingIds'));
        setIsLoading(false);
        return;
      }

      setErrorMessage(null);
      setIsLoading(true);

      try {
        domainStore.setSelectedFamily(familyIdParam);
        if (!familyStore.isLoaded || familyStore.familyId !== familyIdParam) {
          await familyStore.loadFamilyData(familyIdParam);
        }

        const result = await familyStore.fetchPet(familyMemberIdParam);
        if (result.success && result.data) {
          setPetData(result.data);
          const [roleExplanationResult, photoResult] = await Promise.all([
            peopleApi.getRoleExplanation(familyMemberIdParam),
            mediaApi.getPrimaryProfilePhotoDisplay(familyIdParam, familyMemberIdParam, '200x200')
          ]);
          setMemberAccess(
            memberAccessFromRoleExplanation(roleExplanationResult.success ? roleExplanationResult.data : null)
          );
          if (photoResult.success && photoResult.data) {
            setPhotoUrl(photoResult.data.url);
            setPhotoDisplayStatus(photoResult.data.displayStatus);
          } else {
            setPhotoUrl(null);
            setPhotoDisplayStatus(null);
          }
        } else if (!result.success) {
          setErrorMessage(result.error?.getUserMessage?.() ?? t('petProfile.loadFailed'));
        }
      } catch {
        setErrorMessage(t('petProfile.loadFailedRetry'));
      } finally {
        setIsLoading(false);
      }
    };

    loadPetData();
  }, [domainStore, familyStore, familyIdParam, familyMemberIdParam, t]);

  const handleBack = () => {
    if (!familyIdParam || !familyMemberIdParam) return;

    const storageKey = `profileBackFrom_${familyIdParam}`;

    const resolveState = (): ProfileLocationState | undefined => {
      if (locationState?.from) return locationState;

      try {
        const raw = sessionStorage.getItem(storageKey);
        if (!raw) return undefined;
        const parsed = JSON.parse(raw) as
          | {
              screen?: string;
              familyMemberId?: string;
              from?: ProfileLocationState['from'];
              originalAnchorId?: string;
            }
          | undefined;

        if (parsed?.screen === 'pets' && parsed.familyMemberId === familyMemberIdParam && parsed.from) {
          return { from: parsed.from, originalAnchorId: parsed.originalAnchorId };
        }
      } catch {
        // Ignore storage errors/parse issues.
      }

      return undefined;
    };

    const resolved = resolveState();
    const from = resolved?.from;

    try {
      sessionStorage.removeItem(storageKey);
    } catch {
      // ignore
    }

    const getDefaultAnchorId = () => {
      const ownerId = (
        petData as
          | (PetResponse & {
              owners?: { ownerFamilyMemberId: string }[];
            })
          | null
      )?.owners?.[0]?.ownerFamilyMemberId;
      return ownerId ?? familyMemberIdParam;
    };

    if (from === 'members') {
      navigate(`/families/${familyIdParam}/members`);
      return;
    }

    const anchorId = from === 'tree' && resolved?.originalAnchorId ? resolved.originalAnchorId : getDefaultAnchorId();
    navigate(`/families/${familyIdParam}/hybrid-pedigree/${anchorId}`);
  };

  const handleViewTree = () => {
    if (familyIdParam && familyMemberIdParam) {
      navigate(`/families/${familyIdParam}/hybrid-pedigree/${familyMemberIdParam}`);
    }
  };

  const handleEdit = async () => {
    if (!familyMemberIdParam) return;
    const roleResult = await peopleApi.getRoleExplanation(familyMemberIdParam);
    const access = memberAccessFromRoleExplanation(roleResult.success ? roleResult.data : null);
    if (!access.canDirectModify) return;
    setMemberAccess(access);
    setIsEditMode(true);
  };

  const handleCancel = () => {
    setIsEditMode(false);
    if (familyMemberIdParam) {
      void (async () => {
        const result = await familyStore.fetchPet(familyMemberIdParam);
        if (result.success && result.data) setPetData(result.data);
      })();
    }
  };

  const handleSave = async (updatedData: PetResponse) => {
    setPetData(updatedData);
    setIsEditMode(false);
    if (familyIdParam) {
      await familyStore.loadFamilyData(familyIdParam, true);
    }
  };

  const handlePhotoUploadSuccess = async () => {
    if (familyIdParam && familyMemberIdParam) {
      const photoResult = await mediaApi.getPrimaryProfilePhotoDisplay(familyIdParam, familyMemberIdParam, '200x200');
      if (photoResult.success && photoResult.data) {
        setPhotoUrl(photoResult.data.url);
        setPhotoDisplayStatus(photoResult.data.displayStatus);
      } else {
        setPhotoUrl(null);
        setPhotoDisplayStatus(null);
      }
    }
  };

  const handleDeleteClick = async () => {
    if (!familyMemberIdParam || !familyIdParam) return;
    const message = t('petProfile.confirmDelete');
    if (!window.confirm(message)) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      const result = await petsApi.deletePet(familyMemberIdParam);
      if (result.success) {
        await familyStore.loadFamilyData(familyIdParam, true);
        navigate(`/families/${familyIdParam}/members`);
      } else {
        setDeleteError(result.error?.getUserMessage?.() ?? t('petProfile.deleteFailed'));
      }
    } catch {
      setDeleteError(t('petProfile.deleteFailedRetry'));
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="person-profile-screen">
        <div className="person-profile-screen__loading">
          <div className="person-profile-screen__spinner" />
          <p className="person-profile-screen__loading-text">{t('petProfile.loading')}</p>
        </div>
      </div>
    );
  }

  if (errorMessage || !petData) {
    return (
      <div className="person-profile-screen">
        <div className="person-profile-screen__error">
          <h3 className="person-profile-screen__error-title">{errorMessage ?? t('petProfile.notFound')}</h3>
          <button type="button" onClick={handleBack} className="person-profile-screen__back-button">
            {tCommon('back')}
          </button>
        </div>
      </div>
    );
  }

  if (isEditMode) {
    return (
      <div className="person-profile-screen">
        <PetEditForm
          petData={petData}
          onSave={handleSave}
          onCancel={handleCancel}
          onOwnersChanged={async () => {
            if (!familyMemberIdParam) return;
            const result = await familyStore.fetchPet(familyMemberIdParam);
            if (result.success && result.data) setPetData(result.data);
          }}
        />
      </div>
    );
  }

  const owners: PetProfileOwnerLink[] =
    (
      petData as PetResponse & {
        owners?: PetProfileOwnerLink[];
      }
    ).owners?.map((o) => ({
      id: o.id,
      ownerFamilyMemberId: o.ownerFamilyMemberId,
      role: o.role
    })) ?? [];

  const isDeceased = petData.livingStatus === 'deceased';
  const birthOrAdoptionDate = petData.birthDate ?? petData.adoptionDate;
  const showDeathLine = isDeceased && !!petData.deathDate;

  const lifespanText = (() => {
    const birthYear = calendarYearFromStoredDate(birthOrAdoptionDate);
    if (!birthYear) return null;
    if (isDeceased) {
      const deathYear = calendarYearFromStoredDate(petData.deathDate);
      if (deathYear != null) return t('petProfile.yearsSpan', { count: deathYear - birthYear });
      return null;
    }
    if (birthOrAdoptionDate) {
      const birth = localCalendarDateFromStored(birthOrAdoptionDate);
      if (birth) {
        const d = duration(birth, new Date());
        if (d) return t('petProfile.ageOld', { duration: d });
      }
    }
    return t('petProfile.yearsOld', { count: new Date().getFullYear() - birthYear });
  })();

  const kindLabel = formatPetKind(petData.category, petData.subType);
  const sexLabel = formatSexLabel(petData.sex);
  const { effectiveRole, canDirectModify, accessMode, canManageAccess } = memberAccess;
  const accessPrefix = getMemberAccessPrefix(accessMode);
  const canDelete = canDirectModify && !!familyIdParam && !!familyMemberIdParam;

  return (
    <div className="person-profile-screen profile-page">
      <main className="profile-page__main">
        <div className="profile-page__tour-intro">
          <p className="page-intro profile-page__lede">
            {t('petProfile.lede')} <ScreenHelpDot />
          </p>
        </div>
        {deleteError && (
          <div className="person-profile-screen__delete-error" role="alert">
            {deleteError}
            <button
              type="button"
              onClick={() => setDeleteError(null)}
              className="person-profile-screen__delete-error-dismiss"
              aria-label={tForms('actions.dismiss')}
            >
              ×
            </button>
          </div>
        )}

        <div className="profile-hero profile-hero--pet">
          <button
            type="button"
            className="profile-hero__perm"
            onClick={() => setShowRoleExplanation(true)}
            title={tForms('person.permissionTitle')}
          >
            {canDirectModify ? <Unlock width={11} height={11} /> : <Lock width={11} height={11} />}
            <span>{accessPrefix}</span>
            <strong>{effectiveRoleLabel(effectiveRole)}</strong>
          </button>

          <div className="profile-hero__photo-wrap">
            <ProfilePhotoUpload
              familyId={familyIdParam!}
              familyMemberId={familyMemberIdParam!}
              canModify={canDirectModify}
              photoUrl={photoUrl}
              photoDisplayStatus={photoDisplayStatus}
              onUploadSuccess={handlePhotoUploadSuccess}
              variant="profile"
              placeholderInitials={petProfileInitials(petData.displayName)}
            />
          </div>

          <div className="profile-hero__info">
            <h1 className="profile-hero__name">{petData.displayName}</h1>

            {petData.birthDate && (
              <div className="profile-hero__line">
                <span>
                  {tCommon('genealogy.bornDate', {
                    date: formatTemporal(petData.birthDate, (petData.birthDatePrecision as DatePrecision) ?? 'day')
                  })}
                </span>
              </div>
            )}

            {!petData.birthDate && petData.adoptionDate && (
              <div className="profile-hero__line">
                <span>
                  {t('petProfile.adopted', {
                    date: formatTemporal(
                      petData.adoptionDate,
                      (petData.adoptionDatePrecision as DatePrecision) ?? 'day'
                    )
                  })}
                </span>
              </div>
            )}

            {showDeathLine && petData.deathDate && (
              <div className="profile-hero__line">
                <span>
                  {tCommon('genealogy.diedDate', {
                    date: formatTemporal(petData.deathDate, (petData.deathDatePrecision as DatePrecision) ?? 'day')
                  })}
                </span>
              </div>
            )}

            {lifespanText && <div className="profile-hero__lifespan">{lifespanText}</div>}

            <div className="profile-hero__chips">
              <span className="profile-chip">{kindLabel}</span>
              {sexLabel && <span className="profile-chip">{sexLabel}</span>}
              {isDeceased && <span className="profile-chip profile-chip--deceased">{tForms('person.deceased')}</span>}
            </div>

            <div className="profile-hero__actions">
              <button type="button" className="btn btn--ghost btn--sm" onClick={handleViewTree}>
                <Target width={14} height={14} />
                {tForms('pet.viewInTree')}
              </button>
              {canDirectModify ? (
                <button type="button" className="btn btn--primary btn--sm" onClick={handleEdit}>
                  <Pencil width={14} height={14} />
                  {tForms('pet.editProfile')}
                </button>
              ) : null}
              <ProfileMoreMenu
                canDelete={canDelete}
                isDeleting={isDeleting}
                showSuggest={false}
                canManageAccess={canManageAccess}
                onDelete={handleDeleteClick}
                onSuggest={() => undefined}
                onPermissions={() => setShowRoleExplanation(true)}
                onManageAccess={() => setShowManageAccess(true)}
              />
            </div>
          </div>
        </div>

        <PetProfileOwners owners={owners} familyId={familyIdParam ?? undefined} />
      </main>

      {familyMemberIdParam && (
        <RoleExplanationModal
          familyMemberId={familyMemberIdParam}
          isOpen={showRoleExplanation}
          onClose={() => setShowRoleExplanation(false)}
        />
      )}

      {familyIdParam && familyMemberIdParam && (
        <ManageMemberAccessModal
          familyId={familyIdParam}
          familyMemberId={familyMemberIdParam}
          memberLabel={petData?.displayName?.trim() || t('petProfile.thisPet')}
          canManage={canManageAccess}
          actorEffectiveRole={effectiveRole}
          isOpen={showManageAccess}
          onClose={() => setShowManageAccess(false)}
        />
      )}
    </div>
  );
});

export default PetProfileScreen;
